import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Loading from '../../../components/common/Loading/Loading';
import SearchFilterBar from '../../../components/common/SearchFilterBar/SearchFilterBar';
import EmptyState from '../../../components/common/EmptyState/EmptyState';
import ConfirmModal from '../../../components/common/ConfirmModal/ConfirmModal';
import DeleteStudentConfirmModal from '../../../components/students/DeleteStudentConfirmModal/DeleteStudentConfirmModal';
import StudentInactivationReasonModal from '../../../components/students/StudentInactivationReasonModal/StudentInactivationReasonModal';
import { getStudentInactivationReasonLabel } from '../../../constants/studentInactivationReason';
import Pagination from '../../../components/common/Pagination/Pagination';
import KebabMenu from '../../../components/common/KebabMenu/KebabMenu';
import DataTable from '../../../components/common/DataTable/DataTable';
import dataTableStyles from '../../../components/common/DataTable/DataTable.module.css';
import Button from '../../../components/common/Button/Button';
import Icon from '../../../components/common/Icon/Icon';
import RecordCount from '../../../components/common/RecordCount/RecordCount';
import PageSizeSelect from '../../../components/common/PageSizeSelect/PageSizeSelect';
import SingleSelectFilter from '../../../components/common/SingleSelectFilter/SingleSelectFilter';
import MultiSelectFilter from '../../../components/common/MultiSelectFilter/MultiSelectFilter';
import SearchableSelect from '../../../components/common/SearchableSelect/SearchableSelect';
import Badge from '../../../components/common/Badge/Badge';
import ProjectBadge from '../../../components/common/ProjectBadge/ProjectBadge';
import ReactivatedBadge from '../../../components/common/ReactivatedBadge/ReactivatedBadge';
import SensitiveStudentBadge from '../../../components/common/SensitiveStudentBadge/SensitiveStudentBadge';
import PregnantStudentBadge from '../../../components/common/PregnantStudentBadge/PregnantStudentBadge';
import RedFlagBadge from '../../../components/common/RedFlagBadge/RedFlagBadge';
import Tooltip from '../../../components/common/Tooltip';
import FilterChipsBar from '../../../components/common/FilterChipsBar/FilterChipsBar';
import {
  buildOptionFilterChips,
  compactFilterChips,
} from '../../../components/common/FilterChipsBar/buildFilterChips';
import { useStudents, StudentFilters } from '../../../hooks/useStudents';
import { useMarketingProjects } from '../../../hooks/useMarketingProjects';
import { usePageSize } from '../../../hooks/usePageSize';
import { queryFilter, useQueryFilters } from '../../../hooks/useQueryFilters';
import { CoachService, StudentService } from '../../../services';
import { getStudentOriginLabel } from '../../../types/student';
import { maskPhoneByCountry, maskCpf } from '../../../utils/masks';
import {
  STUDENT_SENSITIVE_STATUS_FILTER_OPTIONS,
  STUDENT_SENSITIVE_STATUS_FILTERS,
  type StudentSensitiveStatusFilter,
} from '../../../utils/studentSensitiveStatusFilter';
import FiltersGroup from '../../../components/common/FiltersGroup/FiltersGroup';
import ToolbarRow from '../../../components/common/ToolbarRow/ToolbarRow';
import { useAuth } from '../../../contexts/AuthContext';
import {
  canChangeStudentCoach,
  canInactivateStudents,
  canManageStudents,
} from '../../../utils/permissions';
import { shouldDisplayStudentClassificationBadge, getStudentClassificationBadgeVariant } from '../../../types';
import styles from './StudentList.module.css';

type CoachEditModalState = {
  studentId: number;
  studentName: string;
  coachId: string;
};

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'Todas' },
  { value: 'active', label: 'Ativas' },
  { value: 'inactive', label: 'Inativas' },
];

const STATUS_FILTER_VALUES = ['all', 'active', 'inactive'] as const;
type StudentStatusFilter = typeof STATUS_FILTER_VALUES[number];

const STUDENT_QUERY_FILTERS = {
  page: queryFilter.integer(1, { min: 1 }),
  searchName: queryFilter.string(''),
  status: queryFilter.enum<StudentStatusFilter>('active', STATUS_FILTER_VALUES),
  sortOrder: queryFilter.enum<'asc' | 'desc'>('asc', ['asc', 'desc']),
  coachIds: queryFilter.array<string>([]),
  projectCodes: queryFilter.array<string>([]),
  sensitiveStatuses: queryFilter.array<StudentSensitiveStatusFilter>([], {
    allowed: STUDENT_SENSITIVE_STATUS_FILTERS,
  }),
  redFlag: queryFilter.enum<'all' | 'yes'>('all', ['all', 'yes']),
};

const RED_FLAG_FILTER_OPTIONS = [
  { value: 'all', label: 'Todas' },
  { value: 'yes', label: 'Atenção no treino' },
] as const;

type InactivateTarget = { id: number; name: string } | null;

const StudentList: React.FC = () => {
  const navigate = useNavigate();
  const { roles } = useAuth();
  const canManage = canManageStudents(roles);
  const canInactivate = canInactivateStudents(roles);
  const canChangeCoach = canChangeStudentCoach(roles);
  const {
    filters: {
      searchName,
      status: filterStatus,
      sortOrder,
      coachIds,
      projectCodes,
      sensitiveStatuses,
      redFlag,
    },
    setFilter,
  } = useQueryFilters(STUDENT_QUERY_FILTERS, { pageKey: 'page' });
  const [coachOptions, setCoachOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [loadingCoachOptions, setLoadingCoachOptions] = useState(false);
  const projects = useMarketingProjects();
  const {
    page,
    pageSize,
    pageSizeOptions,
    handlePageChange,
    handlePageSizeChange,
  } = usePageSize('students_page_size');

  const coachOptionsMap = useMemo(
    () => new Map(coachOptions.map((option) => [option.value, option.label])),
    [coachOptions],
  );

  const handleRemoveCoachChip = useCallback((value: string) => {
    setFilter('coachIds', (current) => current.filter((coachId) => coachId !== value));
  }, [setFilter]);

  const projectFilterOptions = useMemo(
    () => projects.map((project) => ({
      value: project.code,
      label: project.code,
      color: project.colors.background,
    })),
    [projects],
  );

  const filterChips = useMemo(() => compactFilterChips(
    coachIds.map((coachId) => ({
      filterKey: 'coach',
      value: coachId,
      label: coachOptionsMap.get(coachId) ?? coachId,
      onRemove: handleRemoveCoachChip,
    })),
    buildOptionFilterChips({
      filterKey: 'projectCodes',
      values: projectCodes,
      options: projectFilterOptions,
      labelPrefix: 'Projeto',
      onRemove: (value) => setFilter(
        'projectCodes',
        (current) => current.filter((item) => item !== value),
      ),
    }),
    buildOptionFilterChips({
      filterKey: 'sensitiveStatuses',
      values: sensitiveStatuses,
      options: STUDENT_SENSITIVE_STATUS_FILTER_OPTIONS,
      labelPrefix: 'Sensibilidade',
      onRemove: (value) => setFilter(
        'sensitiveStatuses',
        (current) => current.filter((item) => item !== value),
      ),
    }),
  ), [
    coachIds,
    coachOptionsMap,
    handleRemoveCoachChip,
    projectCodes,
    projectFilterOptions,
    sensitiveStatuses,
    setFilter,
  ]);

  useEffect(() => {
    let isCancelled = false;

    const loadCoaches = async () => {
      try {
        setLoadingCoachOptions(true);
        const options = await CoachService.listOptions();

        if (isCancelled) {
          return;
        }

        setCoachOptions(options.map((option) => ({ value: String(option.id), label: option.label })));
      } catch {
        if (!isCancelled) {
          setCoachOptions([]);
        }
      } finally {
        if (!isCancelled) {
          setLoadingCoachOptions(false);
        }
      }
    };

    void loadCoaches();

    return () => {
      isCancelled = true;
    };
  }, []);

  const filters: StudentFilters = useMemo(
    () => {
      let filterIsInactive: string | undefined;
      if (filterStatus === 'inactive') filterIsInactive = 'true';
      else if (filterStatus === 'active') filterIsInactive = 'false';
      return {
        searchName: searchName || undefined,
        filterIsInactive,
        sortOrder,
        coachIds: coachIds.length > 0 ? coachIds : undefined,
        projectCodes: projectCodes.length > 0 ? projectCodes : undefined,
        sensitiveStatuses: sensitiveStatuses.length > 0 ? sensitiveStatuses : undefined,
        redFlag,
      };
    },
    [searchName, filterStatus, sortOrder, coachIds, projectCodes, sensitiveStatuses, redFlag]
  );

  const {
    students,
    pagination,
    loading,
    error,
    refresh,
  } = useStudents(pageSize, filters, page);

  const [deleteTarget, setDeleteTarget] = useState<{ id: number; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [inactivateTarget, setInactivateTarget] = useState<InactivateTarget>(null);
  const [isInactivateReasonModalOpen, setIsInactivateReasonModalOpen] = useState(false);
  const [inactivationReason, setInactivationReason] = useState('');
  const [inactivationObservation, setInactivationObservation] = useState<string | null>(null);
  const [inactivating, setInactivating] = useState(false);
  const [pendingCoachEdit, setPendingCoachEdit] = useState<CoachEditModalState | null>(null);
  const [submittingCoachEdit, setSubmittingCoachEdit] = useState(false);

  const handleCoachEditConfirm = useCallback(async () => {
    if (!pendingCoachEdit) {
      return;
    }

    const selectedCoachId = Number.parseInt(pendingCoachEdit.coachId, 10);
    if (!Number.isInteger(selectedCoachId) || selectedCoachId <= 0) {
      alert('Selecione uma professora.');
      return;
    }

    setSubmittingCoachEdit(true);
    try {
      await StudentService.upsertCoachAssociation(pendingCoachEdit.studentId, selectedCoachId);
      setPendingCoachEdit(null);
      refresh();
    } catch (coachEditError) {
      alert(coachEditError instanceof Error ? coachEditError.message : 'Não foi possível atualizar a professora.');
    } finally {
      setSubmittingCoachEdit(false);
    }
  }, [pendingCoachEdit, refresh]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await StudentService.deleteWithRelations(deleteTarget.id);
      setDeleteTarget(null);
      refresh();
    } catch {
      alert('Erro ao excluir aluna. Tente novamente.');
    } finally {
      setDeleting(false);
    }
  };

  const handleConfirmInactivate = async () => {
    if (!inactivateTarget) return;
    try {
      setInactivating(true);
      await StudentService.updateStatus(
        inactivateTarget.id,
        true,
        inactivationReason,
        inactivationObservation,
      );
      setInactivateTarget(null);
      setInactivationReason('');
      setInactivationObservation(null);
      refresh();
    } catch (err: any) {
      setInactivateTarget(null);
      setInactivationReason('');
      setInactivationObservation(null);
      alert(err.message || 'Erro ao inativar aluna. Tente novamente.');
    } finally {
      setInactivating(false);
    }
  };

  const handleInactivateReasonProceed = (reason: string, observation: string | null) => {
    setInactivationReason(reason);
    setInactivationObservation(observation);
    setIsInactivateReasonModalOpen(false);
  };

  const handleCancelInactivateFlow = () => {
    setIsInactivateReasonModalOpen(false);
    setInactivateTarget(null);
    setInactivationReason('');
    setInactivationObservation(null);
  };

  const handleStatusChange = useCallback(async (studentId: number, studentName: string, value: string) => {
    if (value === 'true') {
      setInactivationReason('');
      setInactivationObservation(null);
      setInactivateTarget({ id: studentId, name: studentName });
      setIsInactivateReasonModalOpen(true);
      return;
    }
    try {
      await StudentService.updateStatus(studentId, false);
      refresh();
    } catch {
      alert('Erro ao alterar status da aluna. Tente novamente.');
    }
  }, [refresh]);

  return (
    <div>
      <ToolbarRow>
        {canManage && (
          <Button
            variant="primary"
            size="small"
            className={styles['btn-nova-aluna']}
            onClick={() => navigate('/alunas/nova')}
          >
            Nova Aluna
          </Button>
        )}

        <RecordCount label="Alunas cadastradas:" count={pagination.totalRecords} />

        <FiltersGroup>
          <SearchFilterBar
            searchValue={searchName}
            onSearchChange={(value) => setFilter('searchName', value)}
            searchPlaceholder="Pesquisar"
            loading={loading}
          />

          <SingleSelectFilter
            value={filterStatus}
            onChange={(value) => setFilter('status', value as StudentStatusFilter)}
            options={STATUS_FILTER_OPTIONS}
            placeholder="Status"
          />

          <MultiSelectFilter
            values={coachIds}
            onChange={(values) => setFilter('coachIds', values)}
            options={coachOptions}
            placeholder="Professora"
            searchPlaceholder="Pesquisar"
            disabled={loadingCoachOptions}
            noResultsText="Nenhuma professora encontrada"
            clearButtonLabel="Limpar professoras"
            hideChips
            showSelectionCount={false}
          />

          {projectFilterOptions.length > 0 && (
            <MultiSelectFilter
              values={projectCodes}
              onChange={(values) => setFilter('projectCodes', values)}
              options={projectFilterOptions}
              placeholder="Projeto"
              searchPlaceholder="Pesquisar"
              noResultsText="Nenhum projeto encontrado"
              clearButtonLabel="Limpar projetos"
              hideChips
              showSelectionCount={false}
            />
          )}

          <MultiSelectFilter
            values={sensitiveStatuses}
            onChange={(values) => setFilter(
              'sensitiveStatuses',
              values as StudentSensitiveStatusFilter[],
            )}
            options={STUDENT_SENSITIVE_STATUS_FILTER_OPTIONS}
            placeholder="Sensibilidade"
            searchPlaceholder="Pesquisar"
            noResultsText="Nenhum status encontrado"
            clearButtonLabel="Limpar sensibilidade"
            hideChips
            showSelectionCount={false}
          />

          <SingleSelectFilter
            value={redFlag}
            onChange={(value) => setFilter('redFlag', value as 'all' | 'yes')}
            options={[...RED_FLAG_FILTER_OPTIONS]}
            placeholder="Atenção no treino"
          />
        </FiltersGroup>
      </ToolbarRow>

      <FilterChipsBar chips={filterChips} />

      <div className={styles['list-body']}>
        {error ? (
          <div className="text-center py-5">
            <p className="text-danger">{error}</p>
          </div>
        ) : loading ? (
          <Loading />
        ) : students.length === 0 ? (
          <EmptyState message="Nenhuma aluna encontrada com os filtros selecionados." />
        ) : (
          <DataTable
            className={styles['student-table']}
            headers={[
              <span
                key="nome"
                className={styles['sortable-header']}
                onClick={() => setFilter('sortOrder', (order) => (order === 'asc' ? 'desc' : 'asc'))}
              >
                Nome
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
                  {sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward'}
                </span>
              </span>,
              'CPF', 'Telefone', 'Endereço', 'Origem', 'Status',
              <span key="acoes" className={dataTableStyles['actions-header']}>Ações</span>,
            ]}
            gridTemplate="minmax(180px, 1.2fr) 140px 180px minmax(240px, 2.5fr) 110px 130px 140px"
            highlightTerm={searchName}
          >
                {students.map((s) => {
                  const callingCode = s.country_code || '55';
                  const phoneText = s.phone
                    ? `+${callingCode} ${maskPhoneByCountry(s.phone, callingCode)}`
                    : '—';

                  const addressFields = [s.zipcode, s.country, s.city, s.state, s.street, s.street_number, s.complement, s.neighborhood];

                  const formatAddress = () => {
                    const parts: string[] = [];
                    const streetLine = [s.street, s.street_number].filter(Boolean).join(', ');
                    if (streetLine) {
                      parts.push(s.complement ? `${streetLine} - ${s.complement}` : streetLine);
                    }
                    if (s.neighborhood) parts.push(s.neighborhood);
                    const cityState = [s.city, s.state].filter(Boolean).join('/');
                    if (cityState) parts.push(cityState);
                    if (s.zipcode) parts.push(`CEP: ${s.zipcode}`);
                    return parts.join(' · ') || '—';
                  };
                  const addressText = formatAddress();

                  const isCpfWarning = (s.cpf?.replace(/\D/g, '') ?? '').length !== 11;
                  const isAddressWarning = addressFields.filter(Boolean).length < 4;

                  return (
                  <DataTable.Row
                    key={s.id}
                    className={styles['student-row']}
                    sensitive={Boolean(s.is_sensitive)}
                  >
                    <div className={styles['cell-name-wrap']}>
                      <div className={styles['student-name-line']}>
                        <button
                          type="button"
                          className={`${styles['cell-name']} ${styles['cell-name-link']}`}
                          onClick={() => navigate(`/alunas/${s.id}/editar`)}
                        >
                          {s.full_name}
                        </button>
                      </div>
                      {s.email ? (
                        <span className={styles['student-email']}>{s.email}</span>
                      ) : (
                        <span className={styles['email-empty']}>E-mail não informado</span>
                      )}
                      <div className={styles['coach-row']}>
                        <span className={styles['coach-label']}>Professora:</span>
                        {s.coach_name ? (
                          <span className={styles['coach-name']}>{s.coach_name}</span>
                        ) : (
                          <span className={styles['coach-empty']}>Sem professora</span>
                        )}
                        {canChangeCoach && !s.is_inactive && (
                          <button
                            type="button"
                            className={styles['btn-edit-coach']}
                            title="Alterar professora"
                            aria-label="Alterar professora"
                            onClick={() => setPendingCoachEdit({
                              studentId: s.id,
                              studentName: s.full_name,
                              coachId: s.coach_id ? String(s.coach_id) : '',
                            })}
                          >
                            <Icon name="change_circle" size={16} />
                          </button>
                        )}
                      </div>
                      {(shouldDisplayStudentClassificationBadge(s.classification) || s.project || s.is_reactivated || s.is_sensitive || s.is_pregnant || s.is_red_flag) && (
                        <div className={styles['student-badges']}>
                          {shouldDisplayStudentClassificationBadge(s.classification) && (
                            <Badge
                              variant={getStudentClassificationBadgeVariant(s.classification)}
                              className={styles['classification-badge']}
                            >
                              {s.classification.label}
                            </Badge>
                          )}
                          <ProjectBadge project={s.project} />
                          <ReactivatedBadge visible={s.is_reactivated} />
                          <SensitiveStudentBadge visible={s.is_sensitive} reason={s.sensitive_reason} />
                          <PregnantStudentBadge visible={s.is_pregnant} pregnantUntil={s.pregnant_until} />
                          <RedFlagBadge visible={s.is_red_flag} />
                        </div>
                      )}
                    </div>
                    <div className={styles['cell-nowrap']}>
                      {isCpfWarning && (
                        <Tooltip content="CPF inválido ou incompleto">
                          <span className={styles['warning-icon']} aria-label="CPF inválido ou incompleto">warning</span>
                        </Tooltip>
                      )}
                      <span>{s.cpf ? maskCpf(s.cpf) : '—'}</span>
                    </div>
                    <div className={styles['cell-nowrap']}>
                      {s.phone && (
                        <Tooltip content="Abrir conversa no WhatsApp">
                          <button
                            type="button"
                            className={styles['btn-whatsapp']}
                            aria-label="Abrir conversa no WhatsApp"
                            onClick={() => {
                              const digits = s.phone!.replace(/\D/g, '');
                              if (digits) window.open(`https://wa.me/${callingCode}${digits}`, '_blank', 'noopener');
                            }}
                          >
                            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                            </svg>
                          </button>
                        </Tooltip>
                      )}
                      <span>{phoneText}</span>
                    </div>
                    <div className={styles['cell-address']}>
                      {isAddressWarning && (
                        <Tooltip content="Endereço incompleto (menos de 4 campos preenchidos)">
                          <span
                            className={styles['warning-icon']}
                            aria-label="Endereço incompleto (menos de 4 campos preenchidos)"
                          >
                            warning
                          </span>
                        </Tooltip>
                      )}
                      <span>{addressText}</span>
                    </div>
                    <div className={styles['cell-origin']}>
                      <span>{getStudentOriginLabel(s.origin)}</span>
                    </div>
                    <div className={styles['cell-status']}>
                      <div className={styles['status-line']}>
                        <span className={s.is_inactive ? styles['status-dot-inactive'] : styles['status-dot-active']} />
                        {s.is_inactive ? 'Inativa' : 'Ativa'}
                      </div>
                      {s.is_inactive && s.inactivation_reason && (
                        <div className={styles['inactivation-reason']}>
                          {getStudentInactivationReasonLabel(s.inactivation_reason)}
                          {s.inactivation_observation?.trim()
                            ? ` — ${s.inactivation_observation.trim()}`
                            : ''}
                        </div>
                      )}
                    </div>
                    <div>
                      <div className={styles['actions-cell']}>
                        <Button
                          variant="primary"
                          size="xs"
                          onClick={() => navigate(`/alunas/${s.id}/editar`, { state: { editing: true } })}
                        >
                          Ver aluna
                        </Button>
                        {(canManage || (canInactivate && !s.is_inactive)) && (
                          <KebabMenu
                            items={canManage
                              ? [
                                { label: 'Editar', onClick: () => navigate(`/alunas/${s.id}/editar`, { state: { editing: true } }) },
                                {
                                  label: s.is_inactive ? 'Ativar' : 'Inativar',
                                  onClick: () => handleStatusChange(s.id, s.full_name, s.is_inactive ? 'false' : 'true'),
                                },
                                {
                                  label: 'Excluir',
                                  onClick: () => setDeleteTarget({ id: s.id, name: s.full_name }),
                                },
                              ]
                              : [
                                {
                                  label: 'Inativar',
                                  onClick: () => handleStatusChange(s.id, s.full_name, 'true'),
                                },
                              ]}
                          />
                        )}
                      </div>
                    </div>
                  </DataTable.Row>
                  );
                })}
          </DataTable>
        )}
      </div>

      {students.length > 0 && (
        <div className={styles['bottom-row']}>
          <PageSizeSelect
            value={pageSize}
            options={pageSizeOptions}
            onChange={handlePageSizeChange}
          />
          <div className={styles['pagination-center']}>
            <Pagination
              pagination={pagination}
              loading={loading}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      )}

      <DeleteStudentConfirmModal
        open={canManage && deleteTarget !== null}
        studentName={deleteTarget?.name ?? ''}
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <StudentInactivationReasonModal
        open={(canManage || canInactivate) && isInactivateReasonModalOpen && inactivateTarget !== null}
        studentName={inactivateTarget?.name ?? null}
        onProceed={handleInactivateReasonProceed}
        onCancel={handleCancelInactivateFlow}
      />

      <ConfirmModal
        open={(canManage || canInactivate) && inactivateTarget !== null && !isInactivateReasonModalOpen && !!inactivationReason}
        title="Inativar aluna"
        message={`Tem certeza que deseja inativar ${inactivateTarget?.name ?? 'esta aluna'}? Enquanto inativa, o cadastro não poderá ser editado.`}
        confirmLabel="Inativar"
        cancelLabel="Voltar"
        variant="warning"
        icon="person_off"
        loading={inactivating}
        onConfirm={handleConfirmInactivate}
        onCancel={() => {
          setInactivateTarget(null);
          setInactivationReason('');
        }}
      />

      <ConfirmModal
        open={Boolean(pendingCoachEdit)}
        title={pendingCoachEdit ? `Professora — ${pendingCoachEdit.studentName}` : 'Professora'}
        message={pendingCoachEdit ? 'Selecione a professora responsável por esta aluna.' : ''}
        confirmLabel="Salvar"
        cancelLabel="Cancelar"
        variant="info"
        loading={submittingCoachEdit}
        onCancel={() => {
          if (!submittingCoachEdit) {
            setPendingCoachEdit(null);
          }
        }}
        onConfirm={() => {
          void handleCoachEditConfirm();
        }}
      >
        <div className={styles['coach-modal-fields']}>
          <SearchableSelect
            value={pendingCoachEdit?.coachId ?? ''}
            onChange={(value) => {
              setPendingCoachEdit((current) => (current ? { ...current, coachId: value } : current));
            }}
            options={coachOptions}
            placeholder={loadingCoachOptions ? 'Carregando professoras...' : 'Selecione a professora'}
            searchPlaceholder="Pesquisar"
            noResultsText="Nenhuma professora encontrada"
            disabled={submittingCoachEdit || loadingCoachOptions}
          />
        </div>
      </ConfirmModal>
    </div>
  );
};

export default StudentList;
